import {useEffect, useRef, useState} from "react"
import {useNavigate, useParams} from "react-router-dom"
import {Dropdown} from "primereact/dropdown"
import {MultiSelect} from "primereact/multiselect"
import {Skeleton} from "primereact/skeleton"
import Button from "react-bootstrap/Button"
import {default as cx} from "classnames"
import useSWR from "swr"

import SubHeader from "../../SubHeader.jsx"
import RoleTabs from "../../admin/RolesTab.jsx"
import {getToken} from "../../../utilities/useToken.jsx"
import {fetcherGet} from "../../../utilities/general.jsx"
import MetricsOverview from "./MetricsOverview.jsx"
import MetricsCompliance from "./MetricsCompliance.jsx"
import MetricsDemographics from "./MetricsDemographic.jsx"
import MetricsFinancial from "./MetricsFinancial.jsx"
import MetricsPerformance from "./MetricsPerformance.jsx"

export const METRICS_DASHBOARD_TABS = [
    {label: "Overview", slug: "overview"},
    {label: "Compliance", slug: "compliance"},
    {label: "Performance", slug: "performance"},
    {label: "Financial", slug: "financial"},
    {label: "Demographics", slug: "demographics"},
]

const TAB_CONTENT = {
    overview: MetricsOverview,
    compliance: MetricsCompliance,
    performance: MetricsPerformance,
    financial: MetricsFinancial,
    demographics: MetricsDemographics,
}

const TAB_LABELS = METRICS_DASHBOARD_TABS.map((tab) => tab.label)

const DEFAULT_TAB = METRICS_DASHBOARD_TABS[0]

const TAB_STORAGE_KEY = "metricsDashboardTab"
const FILTER_STORAGE_KEY = "metricsDashboardFilters"

const EMPTY_ROWS = []

const findBySlug = (slug) => METRICS_DASHBOARD_TABS.find((tab) => tab.slug === slug)

const findByLabel = (label) => METRICS_DASHBOARD_TABS.find((tab) => tab.label === label)

const loadStoredFilters = () => {
    const defaultFilters = {
        selectedOrgId: null,
        selectedFacilityIds: [],
        selectedUnitIds: [],
        selectedExperienceLevelIds: [],
        fiscalYear: null,

        hasStoredFacilitySelection: false,
        hasStoredUnitSelection: false,
        hasStoredExperienceSelection: false,
    }
    try {
        const storedValue = sessionStorage.getItem(FILTER_STORAGE_KEY)
        if (!storedValue) {
            return defaultFilters
        }

        const stored = JSON.parse(storedValue)

        const hasStoredFacilitySelection = Object.prototype.hasOwnProperty.call(stored, "selectedFacilityIds")
        const hasStoredUnitSelection = Object.prototype.hasOwnProperty.call(stored, "selectedUnitIds")
        const hasStoredExperienceSelection = Object.prototype.hasOwnProperty.call(stored, "selectedExperienceLevelIds")

        return {
            selectedOrgId: stored.selectedOrgId ?? null,
            selectedFacilityIds: stored.selectedFacilityIds ?? [],
            selectedUnitIds: stored.selectedUnitIds ?? [],
            selectedExperienceLevelIds: stored.selectedExperienceLevelIds ?? [],
            fiscalYear: stored.fiscalYear ?? null,

            /*
             * These distinguish "nothing has been initialized"
             * from "the user intentionally selected nothing."
             */
            hasStoredFacilitySelection,
            hasStoredUnitSelection,
            hasStoredExperienceSelection,
        }
    } catch {
        return defaultFilters
    }
}

const saveFilters = (filters) => {
    /*
     * Only save actual filter values. The hasStored flags are
     * initialization metadata and do not need to be persisted.
     */
    sessionStorage.setItem(
        FILTER_STORAGE_KEY,
        JSON.stringify({
            selectedOrgId: filters.selectedOrgId,
            selectedFacilityIds: filters.selectedFacilityIds,
            selectedUnitIds: filters.selectedUnitIds,
            selectedExperienceLevelIds: filters.selectedExperienceLevelIds,
            fiscalYear: filters.fiscalYear,
        })
    )
}

const useMetricsFilterState = () => {
    const [filters, setFilters] = useState(loadStoredFilters)

    const updateFilters = (changes) => {
        setFilters((current) => {
            const updated = {
                ...current,
                ...changes,
            }

            /*
             * Persist immediately instead of waiting for an effect.
             * This prevents navigation from losing the latest update.
             */
            saveFilters(updated)

            return updated
        })
    }

    return {
        filters,
        updateFilters,
    }
}

const TabContent = ({activeTab, filters, loading}) => {
    const Content = TAB_CONTENT[activeTab]

    if (loading) {
        return <Skeleton className="w-100 h-60px" />
    } else {
        return <Content filters={filters} />
    }
}

const MetricsFilters = ({
    showOrganization,
    organizations,
    organizationId,
    facilities,
    units,
    experienceLevels,
    selectedFacilityIds,
    selectedUnitIds,
    selectedExperienceLevelIds,
    fiscalYear,
    fiscalYears,
    currentFiscalYear,
    onOrganizationChange,
    onFacilitiesChange,
    onUnitsChange,
    updateFilters,
}) => {
    const allFacilitiesSelected = facilities.length > 0 && selectedFacilityIds.length === facilities.length
    const allUnitsSelected = units.length > 0 && selectedUnitIds.length === units.length
    const allExperienceLevelsSelected =
        experienceLevels.length > 0 && selectedExperienceLevelIds.length === experienceLevels.length

    return (
        <div className="card mt-3">
            <div className="card-body py-5">
                <div className="row g-8 align-items-end">
                    {showOrganization && (
                        <div className="col-lg-3 col-md-6">
                            <label htmlFor="id-metrics-organization" className="form-label fw-bold">
                                Organization
                            </label>
                            <Dropdown
                                inputId="id-metrics-organization"
                                className="w-100 p-dropdown-tall"
                                options={organizations.map((o) => ({label: o.name, value: o.id}))}
                                value={organizationId}
                                filter={false}
                                placeholder="Select Organization"
                                onChange={(option) => onOrganizationChange(option.value)}
                            />
                        </div>
                    )}
                    <div className="col-lg-3 col-md-6">
                        <label htmlFor="id-metrics-facility" className="form-label fw-bold">
                            Facility
                        </label>
                        <MultiSelect
                            inputId="id-metrics-facility"
                            className="w-100 p-dropdown-tall p-dropdown p-component p-inputwrapper"
                            options={facilities.map((f) => ({label: f.name, value: f.id}))}
                            value={selectedFacilityIds}
                            disabled={facilities.length === 0}
                            placeholder="Select Facility"
                            maxSelectedLabels={allFacilitiesSelected ? 0 : facilities.length}
                            selectedItemsLabel="All Facilities"
                            selectAllLabel="All Facilities"
                            onChange={(selected) => onFacilitiesChange(selected.value)}
                        />
                    </div>
                    <div className="col-lg-3 col-md-6">
                        <label htmlFor="id-metrics-unit" className="form-label fw-bold">
                            Unit(s)
                        </label>
                        <MultiSelect
                            inputId="id-metrics-unit"
                            className="w-100 p-dropdown-tall p-dropdown p-component p-inputwrapper"
                            options={units.map((u) => ({label: u.name, value: u.id}))}
                            value={selectedUnitIds}
                            disabled={units.length === 0}
                            placeholder="Select Unit(s)"
                            maxSelectedLabels={allUnitsSelected ? 0 : units.length}
                            selectedItemsLabel="All Units"
                            selectAllLabel="All Units"
                            onChange={(selected) => {
                                updateFilters({selectedUnitIds: selected.value ?? []})
                            }}
                        />
                    </div>
                    <div className="col-lg-3 col-md-6">
                        <label htmlFor="id-metrics-experience-level" className="form-label fw-bold">
                            Experience Level
                        </label>
                        <MultiSelect
                            inputId="id-metrics-experience-level"
                            className="w-100 p-dropdown-tall p-dropdown p-component p-inputwrapper"
                            options={experienceLevels.map((e) => ({label: e.name, value: e.id}))}
                            value={selectedExperienceLevelIds}
                            disabled={experienceLevels.length === 0}
                            placeholder="Select Experience Level(s)"
                            maxSelectedLabels={allExperienceLevelsSelected ? 0 : experienceLevels.length}
                            selectedItemsLabel="All Experience Levels"
                            selectAllLabel="All Experience Levels"
                            onChange={(selected) => {
                                updateFilters({selectedExperienceLevelIds: selected.value ?? []})
                            }}
                        />
                    </div>
                    <div className="col-12">
                        <label className="form-label fw-bold d-block">Fiscal Year</label>
                        <div
                            className="btn-group w-100 flex-wrap row-gap-2 align-items-center rounded-4"
                            role="tablist"
                        >
                            {fiscalYears.map((year) => (
                                <Button
                                    key={`metrics-fy-${year}`}
                                    className={cx(
                                        "btn btn-light-primary btn-active-primary fw-bold border text-nowrap",
                                        {active: fiscalYear === year}
                                    )}
                                    onClick={() => {
                                        updateFilters({fiscalYear: year})
                                    }}
                                >
                                    {currentFiscalYear === year ? `${year} YTD` : year}
                                </Button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

const MetricsDashboard = () => {
    const {tab: tabParam} = useParams()
    const navigate = useNavigate()

    const {role, organization_id: tokenOrganizationId} = getToken() || {}

    const activeTab = findBySlug(tabParam)
    const isEnterpriseAdmin = role === "Enterprise Admin"

    const {filters: filterState, updateFilters} = useMetricsFilterState()

    const {
        selectedOrgId,
        selectedFacilityIds,
        selectedUnitIds,
        selectedExperienceLevelIds,
        fiscalYear,
        hasStoredFacilitySelection,
        hasStoredUnitSelection,
        hasStoredExperienceSelection,
    } = filterState

    /*
     * These refs only prevent SWR revalidation from overwriting
     * a manual or restored selection.
     */
    const initializedFacilitiesOrgRef = useRef(null)
    const initializedUnitsScopeRef = useRef(null)
    const initializedExperienceOrgRef = useRef(null)

    /*
     * A stored selection must be preserved even when it is [].
     * The empty array may represent an intentional deselect-all.
     */
    const preserveStoredFacilitiesRef = useRef(hasStoredFacilitySelection)
    const preserveStoredUnitsRef = useRef(hasStoredUnitSelection)
    const preserveStoredExperienceRef = useRef(hasStoredExperienceSelection)

    const {data: organizations, isLoading: orgsLoading} = useSWR(
        isEnterpriseAdmin ? "/get-organizations" : null,
        fetcherGet,
        {
            fallbackData: EMPTY_ROWS,
        }
    )

    const organizationId = isEnterpriseAdmin ? selectedOrgId : tokenOrganizationId

    const {data: facilities, isLoading: facsLoading} = useSWR(
        organizationId ? `/organization/${organizationId}/facility` : null,
        fetcherGet,
        {
            fallbackData: EMPTY_ROWS,
        }
    )

    /*
     * - zero selected facilities means no facility restriction
     * - every facility selected means no facility restriction
     * - a subset means restrict to that subset
     */
    const noFacilityRestriction =
        selectedFacilityIds.length === 0 || (facilities.length > 0 && selectedFacilityIds.length === facilities.length)

    const facilityScopeKey = noFacilityRestriction ? "ALL" : [...selectedFacilityIds].sort().join(",")

    const unitUrl = !organizationId
        ? null
        : noFacilityRestriction
          ? `/organization/${organizationId}/unit`
          : `/organization/${organizationId}/unit?facility_ids=${selectedFacilityIds.join(",")}`

    const {data: units, isLoading: unitsLoading} = useSWR(unitUrl, fetcherGet, {
        fallbackData: EMPTY_ROWS,
    })

    const noUnitRestriction =
        selectedUnitIds.length === 0 || (units.length > 0 && selectedUnitIds.length === units.length)

    const {data: experienceLevels, expLoading} = useSWR("/experience-levels", fetcherGet, {
        fallbackData: EMPTY_ROWS,
    })

    // const {data: fiscalYearMeta, fiscLoading} = useSWR(
    //     organizationId ? `/metrics/fiscal-years?organization_id=${organizationId}` : null,
    //     fetcherGet
    // )
    const fiscLoading = false;
    const fiscalYearMeta = {"current":2027,"years":[2027,2026,2025,2024,2023]};


    const loading = orgsLoading || facsLoading || unitsLoading || expLoading || fiscLoading
    const currentFiscalYear = fiscalYearMeta?.current ?? null
    const fiscalYears = fiscalYearMeta?.years ?? EMPTY_ROWS

    useEffect(() => {
        document.title = "Metrics Dashboard"
    }, [])

    useEffect(() => {
        if (activeTab) {
            localStorage.setItem(TAB_STORAGE_KEY, activeTab.slug)

            return
        }

        const storedTab = findBySlug(localStorage.getItem(TAB_STORAGE_KEY))

        navigate(`/metrics/dashboard/${(storedTab ?? DEFAULT_TAB).slug}`, {
            replace: true,
        })
    }, [activeTab, navigate])

    /*
     * Select the first organization when an enterprise administrator
     * does not have a stored organization.
     */
    useEffect(() => {
        if (!isEnterpriseAdmin || selectedOrgId || organizations.length === 0) {
            return
        }

        updateFilters({
            selectedOrgId: organizations[0].id,
        })
    }, [isEnterpriseAdmin, selectedOrgId, organizations])

    /*
     * Restore a valid stored fiscal year. Otherwise, default to
     * the organization's current fiscal year.
     */
    useEffect(() => {
        if (!currentFiscalYear || fiscalYears.length === 0 || fiscalYears.includes(fiscalYear)) {
            return
        }

        updateFilters({
            fiscalYear: currentFiscalYear,
        })
    }, [currentFiscalYear, fiscalYears, fiscalYear])

    /*
     * Default facilities to all facilities only when there is no
     * stored selection for the initial organization.
     *
     * A stored [] is preserved as an intentional deselect-all.
     */
    useEffect(() => {
        if (!organizationId || facilities.length === 0 || initializedFacilitiesOrgRef.current === organizationId) {
            return
        }

        initializedFacilitiesOrgRef.current = organizationId

        if (preserveStoredFacilitiesRef.current) {
            preserveStoredFacilitiesRef.current = false
            return
        }
    }, [organizationId, facilities])

    /*
     * Default units to all units when entering a new facility scope.
     *
     * On the initial load, the stored unit selection is preserved,
     * including an intentionally empty selection.
     */
    useEffect(() => {
        if (units.length === 0 || initializedUnitsScopeRef.current === facilityScopeKey) {
            return
        }

        initializedUnitsScopeRef.current = facilityScopeKey

        if (preserveStoredUnitsRef.current) {
            preserveStoredUnitsRef.current = false
            return
        }
    }, [facilityScopeKey, units])

    /*
     * Default experience levels to all values for a new organization.
     * Preserve a stored empty selection during the initial load.
     */
    useEffect(() => {
        if (
            !organizationId ||
            experienceLevels.length === 0 ||
            initializedExperienceOrgRef.current === organizationId
        ) {
            return
        }

        initializedExperienceOrgRef.current = organizationId

        if (preserveStoredExperienceRef.current) {
            preserveStoredExperienceRef.current = false
            return
        }
    }, [organizationId, experienceLevels])

    const changeTab = (label) => {
        const nextTab = findByLabel(label)

        if (nextTab) {
            navigate(`/metrics/dashboard/${nextTab.slug}`)
        }
    }

    const handleOrganizationChange = (nextOrganizationId) => {
        initializedFacilitiesOrgRef.current = null
        initializedUnitsScopeRef.current = null
        initializedExperienceOrgRef.current = null

        preserveStoredFacilitiesRef.current = false
        preserveStoredUnitsRef.current = false
        preserveStoredExperienceRef.current = false

        updateFilters({
            selectedOrgId: nextOrganizationId,
            selectedFacilityIds: [],
            selectedUnitIds: [],
            selectedExperienceLevelIds: [],
            fiscalYear: null,
        })
    }

    const handleFacilitiesChange = (nextFacilityIds) => {
        /*
         * Mark units as uninitialized because their available values
         * depend on the selected facility scope.
         */
        initializedUnitsScopeRef.current = null
        preserveStoredUnitsRef.current = false

        updateFilters({
            selectedFacilityIds: nextFacilityIds ?? [],
            selectedUnitIds: [],
        })
    }

    const metricsFilters = {
        organization_id: organizationId,
        facility_ids: noFacilityRestriction ? [] : selectedFacilityIds,
        unit_ids: noUnitRestriction ? [] : selectedUnitIds,
        experience_level_ids: selectedExperienceLevelIds,
        fiscal_year: fiscalYear,
    }

    if (!activeTab) {
        return null
    }

    return (
        <>
            <SubHeader title1="OARS" title2="Metrics Dashboard" icon="fa-chart-line" />
            <div className="container-fluid">
                <MetricsFilters
                    showOrganization={isEnterpriseAdmin}
                    organizations={organizations}
                    organizationId={organizationId}
                    facilities={facilities}
                    units={units}
                    experienceLevels={experienceLevels}
                    selectedFacilityIds={selectedFacilityIds}
                    selectedUnitIds={selectedUnitIds}
                    selectedExperienceLevelIds={selectedExperienceLevelIds}
                    fiscalYear={fiscalYear}
                    fiscalYears={fiscalYears}
                    currentFiscalYear={currentFiscalYear}
                    updateFilters={updateFilters}
                    onOrganizationChange={handleOrganizationChange}
                    onFacilitiesChange={handleFacilitiesChange}
                />
                <RoleTabs tabs={TAB_LABELS} active={activeTab.label} onChange={changeTab} />
                <div className="mt-5">
                    {fiscalYear !== null && (
                        <TabContent activeTab={activeTab.slug} filters={metricsFilters} loading={loading} />
                    )}
                </div>
            </div>
        </>
    )
}

export default MetricsDashboard
